import React, { useState } from "react";
import { Menu } from "antd";
import { NavLink, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import {
  DashboardOutlined,
  FileTextOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  ProjectOutlined,
  UserOutlined,
  BankOutlined,
  SolutionOutlined,
  SettingOutlined,
  LogoutOutlined,
  FileDoneOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import { CompanyRoutesConfig } from "../../companyRoutesConfig";
import useUserDetails from "../../utils/useUserDetails";
import "./Sidenav.css";

/* ─────────────────────────────────────────────────────────────
   GROUP CONFIG
   Each group has:
     key        — unique key for the Menu.SubMenu
     label      — display label
     icon       — Ant Design icon
     paths      — array of route names (matches CompanyRoutesConfig[].name)
                  OR a single path string for a direct link (no submenu)
   ───────────────────────────────────────────────────────────── */
const MENU_GROUPS = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: <DashboardOutlined />,
    directPath: "/company/dashboard", // single item — no submenu
    permission: null, // always show
  },
  {
    key: "crm",
    label: "CRM",
    icon: <ProjectOutlined />,
    directPath: "/company/CRM",
    permission: "view_requirements",
  },
  {
    key: "dms",
    label: "Document Management",
    icon: <FileTextOutlined />,
    directPath: "/company/dms",
    permission: null,
  },

  {
    key: "attendance",
    label: "Attendance",
    icon: <ClockCircleOutlined />,
    children: [
      {
        name: "Attendance",
        path: "/company/attendance",
        icon: <ClockCircleOutlined />,
        permission: null,
      },
      {
        name: "My Leave",
        path: "/company/my-leave",
        icon: <CalendarOutlined />,
        permission: null,
      },
      {
        name: "My Correction",
        path: "/company/my-correction",
        icon: <CheckSquareOutlined />,
        permission: null,
      },
      {
        name: "Salary Records",
        path: "/company/salary-summary",
        icon: <ClockCircleOutlined />,
        permission: null,
      },
    ],
  },
  {
    key: "hrm",
    label: "HRM",
    icon: <ClockCircleOutlined />,
    children: [
      {
        name: "Users",
        path: "/company/users",
        icon: <UserOutlined />,
        permission: "hr_manager",
      },
      {
        name: "Attendance Sheet",
        path: "/company/attendance-sheet",
        icon: <ClockCircleOutlined />,
        permission: "hr_manager",
      },
      {
        name: "Attendance Summary",
        path: "/company/attendance-summary",
        icon: <ClockCircleOutlined />,
        permission: "hr_manager",
      },
      {
        name: "Users Leaves",
        path: "/company/leave-management",
        icon: <ClockCircleOutlined />,
        permission: "hr_manager",
      },
      {
        name: "Users Correction",
        path: "/company/correction-management",
        icon: <CheckSquareOutlined />,
        permission: "hr_manager",
      },
    ],
  },
  {
    key: "requirements",
    label: "Requirements",
    icon: <FileTextOutlined />,
    children: [
      {
        name: "Requirements",
        path: "/company/requirements",
        icon: <FileTextOutlined />,
        permission: "view_requirements",
      },
      {
        name: "Candidate",
        path: "/company/consultants",
        icon: <UserOutlined />,
        permission: "view_candidates",
      },
      {
        name: "Candidate-Requirement",
        path: "/company/candidate-requirement",
        icon: <SolutionOutlined />,
        permission: "view_candidates",
      },
      {
        name: "Client",
        path: "/company/client",
        icon: <BankOutlined />,
        permission: "view_client",
      },
    ],
  },
  {
    key: "outreach",
    label: "Outreach",
    icon: <TeamOutlined />,
    children: [
      {
        name: "Job Posting",
        path: "/company/jobposting",
        icon: <TeamOutlined />,
        permission: null,
      },
      {
        name: "Leads",
        path: "/company/leads",
        icon: <TeamOutlined />,
        permission: null,
      },
      {
        name: "SPOC",
        path: "/company/spoclist",
        icon: <UserOutlined />,
        permission: null,
      },
      {
        name: "Accounts",
        path: "/company/jobcompany",
        icon: <BankOutlined />,
        permission: null,
      },
      {
        name: "Opportunities",
        path: "/company/outreach",
        icon: <ProjectOutlined />,
        permission: null,
      },
    ],
  },
  // {
  //   key: "settings",
  //   label: "Settings",
  //   icon: <SettingOutlined />,
  //   directPath: "/company/profile", // single item — no submenu
  //   permission: "company_profile", // always show
  // },
  {
    key: "settings",
    label: "Settings",
    icon: <SettingOutlined />,
    children: [
      {
        name: "Company Profile",
        path: "/company/profile",
        icon: <BankOutlined />,
        permission: "company_profile",
      },
      {
        name: "Users Setting",
        path: "/company/setting",
        icon: <BankOutlined />,
        permission: "company_profile",
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────
   VENDOR MENU — flat, no submenus needed
   ───────────────────────────────────────────────────────────── */
const VENDOR_MENU = [
  {
    key: "v-dashboard",
    label: "Dashboard",
    path: "/vendor/Dashboard",
    icon: <DashboardOutlined />,
  },
  {
    key: "v-requirement",
    label: "Requirement",
    path: "/vendor/requirement",
    icon: <FileTextOutlined />,
  },
  {
    key: "v-applications",
    label: "Your Application",
    path: "/vendor/applications",
    icon: <SolutionOutlined />,
  },
  {
    key: "v-timesheet",
    label: "TimeSheet",
    path: "/vendor/timesheet",
    icon: <ClockCircleOutlined />,
  },
  {
    key: "v-invoices",
    label: "Invoice",
    path: "/vendor/invoices",
    icon: <FileDoneOutlined />,
  },
];

/* ─────────────────────────────────────────────────────────────
   HELPERS
   ───────────────────────────────────────────────────────────── */

// Check if a permission is granted
const hasPermission = (permissions, permission) => {
  if (!permission) return true; // null = always show
  if (!permissions) return false;
  return permissions.includes(permission);
};

// Find which group key contains the current path (for defaultOpenKeys)
const getOpenGroupKey = (pathname, groups) => {
  for (const group of groups) {
    if (group.children) {
      const match = group.children.find((c) => pathname.startsWith(c.path));
      if (match) return group.key;
    }
  }
  return null;
};

/* ─────────────────────────────────────────────────────────────
   COMPONENT
   ───────────────────────────────────────────────────────────── */
export default function Sidenav() {
  const { pathname } = useLocation();

  const userRole = localStorage.getItem("userRole");
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const companyDetails = JSON.parse(
    localStorage.getItem("companyDetails") || "{}",
  );
  const vendorDetails = JSON.parse(
    localStorage.getItem("vendorDetails") || "{}",
  );
  const permissions = userDetails?.roleId?.permissions || [];
  const userData = useUserDetails();

  const defaultOpenKey = getOpenGroupKey(pathname, MENU_GROUPS);

  /* ── logout ─────────────────────────────────────────────── */
  const handleLogout = async () => {
    const result = await Swal.fire({
      title: "Are you sure you want to Logout?",
      text: "You will be logged out from the application!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3699FF",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Logout!",
    });

    if (result.isConfirmed) {
      localStorage.clear();
      await Swal.fire(
        "Logged Out!",
        "You have successfully logged out.",
        "success",
      );
      window.location.href = "/auth/company/login";
    }
  };

  /* ── active key — match current pathname to a menu item ─── */
  const getActiveKey = () => {
    for (const group of MENU_GROUPS) {
      if (group.directPath && pathname.startsWith(group.directPath))
        return group.key;
      if (group.children) {
        const match = group.children.find((c) => pathname.startsWith(c.path));
        if (match) return match.path;
      }
    }
    return "";
  };

  /* ── display name in footer ─────────────────────────────── */
  const displayName =
    userRole === "company"
      ? companyDetails?.name
      : userRole === "vendor"
        ? vendorDetails?.name
        : userDetails?.name;

  /* ── profile link ───────────────────────────────────────── */
  const profilePath =
    userRole === "company" ||
    userData?.permissions?.includes("add_technology_niche")
      ? "/company/profile"
      : "/vendor/profile";

  /* ── build Menu items for company / default role ─────────── */
  const buildCompanyItems = () =>
    MENU_GROUPS.map((group) => {
      // Direct link — no submenu
      if (group.directPath) {
        if (
          !hasPermission(permissions, group.permission) &&
          userRole !== "company"
        )
          return null;
        return {
          key: group.key,
          icon: group.icon,
          label: <NavLink to={group.directPath}>{group.label}</NavLink>,
        };
      }

      // Group with children — filter by permission
      const visibleChildren = group.children.filter(
        (child) =>
          userRole === "company" ||
          hasPermission(permissions, child.permission),
      );

      if (visibleChildren.length === 0) return null;
      // const visibleChildren = group.children;

      // If only one child visible, render as direct link instead of submenu
      if (visibleChildren.length === 1) {
        const c = visibleChildren[0];
        return {
          key: c.path,
          icon: c.icon,
          label: <NavLink to={c.path}>{c.name}</NavLink>,
        };
      }

      return {
        key: group.key,
        icon: group.icon,
        label: group.label,
        children: visibleChildren.map((c) => ({
          key: c.path,
          icon: c.icon,
          label: <NavLink to={c.path}>{c.name}</NavLink>,
        })),
      };
    }).filter(Boolean);

  /* ── build Menu items for vendor ────────────────────────── */
  const buildVendorItems = () =>
    VENDOR_MENU.map((item) => ({
      key: item.key,
      icon: item.icon,
      label: <NavLink to={item.path}>{item.label}</NavLink>,
    }));

  const menuItems =
    userRole === "vendor" ? buildVendorItems() : buildCompanyItems();

  /* ── render ──────────────────────────────────────────────── */
  return (
    <div className="sidenav-wrapper">
      {/* Brand */}
      <div className="brand">
        <span className="brand-name">VETTRO</span>
        <span className="brand-tagline">global hiring intent</span>
      </div>

      <hr className="sidenav-divider" />

      <div className="sidenav-body">
        {/* Navigation */}
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[getActiveKey()]}
          defaultOpenKeys={defaultOpenKey ? [defaultOpenKey] : []}
          items={menuItems}
          className="sidenav-menu ant-menu"
        />

        {/* Footer — user info + logout */}
        <div className="aside-footer">
          <div className="footer-box">
            {userDetails?.avatar ? (
              <img
                src={userDetails.avatar}
                alt="avatar"
                className="footer-avatar"
              />
            ) : (
              <div className="footer-avatar footer-avatar--placeholder">
                {displayName?.charAt(0)?.toUpperCase() || "U"}
              </div>
            )}
            <div className="footer-info">
              {userRole === "company" ||
              userData?.permissions?.includes("add_technology_niche") ? (
                <NavLink to={profilePath} className="footer-name">
                  {displayName}
                </NavLink>
              ) : (
                <span className="footer-name">{displayName}</span>
              )}
              <span className="footer-logout" onClick={handleLogout}>
                <LogoutOutlined style={{ marginRight: 4 }} />
                Logout
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
